import type {
  AuthorityRef,
  CommandReceipt,
  SessionCreateInput,
  SessionDetail,
  SessionListQuery,
  SessionListResult,
  SessionRecoveryInput,
  SessionSummary,
  TerminalHistoryQuery,
  TerminalHistoryResult,
} from "./domain/session.ts";
import { OrniTransportError, type SessionTransport } from "./transport.ts";
import type { ProviderHistoryTransport } from "./provider-history.ts";

/**
 * Stable client boundary for Session features.
 *
 * Pages depend on this contract, never on a Daemon/Cloud implementation. The
 * transport-neutral SDK keeps command identity, receipts and message status
 * identical on both sides of the network boundary.
 */
export interface SessionPort extends ProviderHistoryTransport {
  readonly authority: AuthorityRef;
  listSessions(query?: SessionListQuery): Promise<SessionListResult>;
  getSession(sessionId: string): Promise<SessionSummary>;
  getSessionWorkspace(sessionId: string): Promise<import("./domain/session.ts").SessionWorkspace>;
  createSession(input: SessionCreateInput): Promise<SessionDetail>;
  startSession(
    sessionId: string,
    input?: import("./domain/session.ts").SessionStartInput,
  ): Promise<CommandReceipt>;
  pauseSession(sessionId: string, waitForCompletion?: boolean): Promise<CommandReceipt>;
  recoverSession(sessionId: string, input?: SessionRecoveryInput): Promise<CommandReceipt>;
  stopSession(sessionId: string): Promise<CommandReceipt>;
  restartSession(sessionId: string, waitForCompletion?: boolean): Promise<CommandReceipt>;
  renameSession(sessionId: string, title: string): Promise<CommandReceipt>;
  deleteSession(sessionId: string): Promise<CommandReceipt>;
}

/**
 * Terminal transport is deliberately generic: PTY connection and history
 * representations belong to the adapter until the shared stream is opened.
 * A null result means that the Session has no attachable terminal yet.
 */
export interface TerminalPort<
  Connection = unknown,
  History = TerminalHistoryResult,
  HistoryQuery extends TerminalHistoryQuery = TerminalHistoryQuery,
> {
  history(sessionId: string, query?: HistoryQuery): Promise<History | null>;
  connect(sessionId: string): Promise<Connection | null>;
}

/** Bind a transport once so callers never lose its `this` context. */
export function bindSessionPort(transport: SessionTransport): SessionPort {
  const getSession = typeof transport.getSession === "function"
    ? transport.getSession.bind(transport)
    : async (sessionId: string): Promise<SessionSummary> => {
      const result = await transport.listSessions({ limit: 100 });
      const session = result.sessions.find((item) => item.id === sessionId);
      if (!session) {
        throw new OrniTransportError("NOT_FOUND", "The Session was not found.");
      }
      return session;
    };
  const getSessionWorkspace = typeof transport.getSessionWorkspace === "function"
    ? transport.getSessionWorkspace.bind(transport)
    : async (_sessionId: string): Promise<import("./domain/session.ts").SessionWorkspace> => {
      throw new OrniTransportError("UNSUPPORTED", "Session workspace reads are not available on this transport.");
    };
  return {
    authority: transport.authority,
    listSessions: transport.listSessions.bind(transport),
    getSession,
    getSessionWorkspace,
    createSession: transport.createSession.bind(transport),
    startSession: transport.startSession.bind(transport),
    pauseSession: transport.pauseSession.bind(transport),
    recoverSession: transport.recoverSession.bind(transport),
    stopSession: transport.stopSession.bind(transport),
    restartSession: transport.restartSession.bind(transport),
    renameSession: transport.renameSession.bind(transport),
    deleteSession: transport.deleteSession.bind(transport),
    discoverProviderHistory: transport.discoverProviderHistory.bind(transport),
    scanProviderHistory: transport.scanProviderHistory.bind(transport),
    ...(typeof transport.createProviderHistoryScan === "function" ? {
      createProviderHistoryScan: transport.createProviderHistoryScan.bind(transport),
    } : {}),
    ...(typeof transport.getProviderHistoryScan === "function" ? {
      getProviderHistoryScan: transport.getProviderHistoryScan.bind(transport),
    } : {}),
    ...(typeof transport.cancelProviderHistoryScan === "function" ? {
      cancelProviderHistoryScan: transport.cancelProviderHistoryScan.bind(transport),
    } : {}),
    ...(typeof transport.resumeProviderHistoryScan === "function" ? {
      resumeProviderHistoryScan: transport.resumeProviderHistoryScan.bind(transport),
    } : {}),
    ...(typeof transport.pollProviderHistoryScan === "function" ? {
      pollProviderHistoryScan: transport.pollProviderHistoryScan.bind(transport),
    } : {}),
    readProviderConversation: transport.readProviderConversation.bind(transport),
    importProviderHistory: transport.importProviderHistory.bind(transport),
  };
}
